/*
 *  Licensed under the EUPL, Version 1.2 or – as soon they will be approved by
the European Commission - subsequent versions of the EUPL (the "Licence");
You may not use this work except in compliance with the Licence.
You may obtain a copy of the Licence at:

  https://joinup.ec.europa.eu/software/page/eupl

Unless required by applicable law or agreed to in writing, software
distributed under the Licence is distributed on an "AS IS" basis,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the Licence for the specific language governing permissions and
limitations under the Licence. */

import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useRef, useState } from "react";
import { useIntl } from "react-intl";
import type { MarkerDragEvent } from "react-map-gl/maplibre";
import { Marker } from "react-map-gl/maplibre";
import { StopPlaceActions } from "../../../../actions";
import { moveElementIfAllowed } from "../../../../actions/StopPlaceActions.modern";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import type { CrosshairSetting } from "../crosshair";
import { DragCrosshair, getCrosshairPreference } from "../crosshair";
import { useCanEditOnMap } from "../hooks/useCanEditOnMap";
import { useCheckedMove } from "../hooks/useCheckedMove";
import { useMapCurrentStop } from "../hooks/useMapCurrentStop";
import { useMarkerScale } from "../hooks/useMarkerScale";
import { MarkerCoordinatesDialog } from "./MarkerCoordinatesDialog";
import { MarkerPopup } from "./MarkerPopup";
import type {
  BoardingPosition,
  FocusedBoardingPosition,
  FocusedElement,
  LatLng,
} from "./types";

const BP_SIZE = 26;

interface BoardingPositionItemProps {
  boardingPosition: BoardingPosition;
  bpIndex: number;
  quayIndex: number;
  focused: boolean;
  /** Read-only users can neither drag nor retype the position. */
  disabled: boolean;
  /** The quay's position, which typed coordinates are checked against. */
  quayLocation?: LatLng;
}

const BoardingPositionItem = ({
  boardingPosition,
  bpIndex,
  quayIndex,
  focused,
  disabled,
  quayLocation,
}: BoardingPositionItemProps) => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const [popupAnchor, setPopupAnchor] = useState<HTMLElement | null>(null);
  const [isEditingCoordinates, setIsEditingCoordinates] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const crosshairRef = useRef<CrosshairSetting>("none");
  const scale = useMarkerScale();
  const { pendingPosition, moveTo } = useCheckedMove((position) =>
    dispatch(
      moveElementIfAllowed(
        { markerIndex: bpIndex, type: "boarding-position", quayIndex },
        position,
      ),
    ),
  );

  if (!boardingPosition.location) return null;

  const [lat, lng] = pendingPosition ?? boardingPosition.location;
  const label = boardingPosition.publicCode ?? "";

  const handleDragStart = () => {
    crosshairRef.current = getCrosshairPreference();
    setIsDragging(true);
  };

  const handleDragEnd = (event: MarkerDragEvent) => {
    setIsDragging(false);
    moveTo([event.lngLat.lat, event.lngLat.lng]);
  };

  const showCrosshair = isDragging && crosshairRef.current !== "none";

  return (
    <>
      <Marker
        latitude={lat}
        longitude={lng}
        draggable={!disabled}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        anchor="center"
      >
        {showCrosshair ? (
          <DragCrosshair
            type={crosshairRef.current as Exclude<CrosshairSetting, "none">}
          />
        ) : (
          <Box
            onClick={(e) => {
              // Focus the boarding position so the quay panel jumps to its
              // Boarding Positions tab (QuayPanel watches this focus state).
              dispatch(
                StopPlaceActions.setBoardingPositionElementFocus(
                  bpIndex,
                  quayIndex,
                ),
              );
              setPopupAnchor(e.currentTarget);
            }}
            sx={(theme) => ({
              width: Math.round(BP_SIZE * scale),
              height: Math.round(BP_SIZE * scale),
              borderRadius: "50%",
              // Focus is shown by the ring and scale only, never by recolouring —
              // matches QuayMarkers and ParkingMarkers so the base colour always
              // identifies the element type.
              bgcolor: "background.paper",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              border: "2px solid",
              borderColor: "secondary.main",
              boxShadow: focused
                ? `0 0 0 2px ${alpha(theme.palette.warning.main, 0.5)}, 0 2px 4px rgba(0,0,0,0.4)`
                : "0 1px 3px rgba(0,0,0,0.35)",
              transform: focused ? "scale(1.2)" : "none",
              transition: "all 0.15s",
              "&:hover": { transform: "scale(1.25)" },
            })}
          >
            <Typography
              sx={{
                color: "secondary.main",
                fontWeight: 800,
                fontSize: `${0.7 * scale}rem`,
                lineHeight: 1,
                letterSpacing: "0.01em",
                userSelect: "none",
              }}
            >
              {label}
            </Typography>
          </Box>
        )}
      </Marker>

      <MarkerPopup
        anchorEl={popupAnchor}
        onClose={() => setPopupAnchor(null)}
        title={`${formatMessage({ id: "boarding_positions_item_header" })} ${label}`}
        id={boardingPosition.id}
        lat={lat}
        lng={lng}
        minWidth={180}
        onEditCoordinates={
          disabled
            ? undefined
            : () => {
                setPopupAnchor(null);
                setIsEditingCoordinates(true);
              }
        }
      />
      <MarkerCoordinatesDialog
        open={isEditingCoordinates}
        lat={lat}
        lng={lng}
        onClose={() => setIsEditingCoordinates(false)}
        onSubmit={moveTo}
        reference={
          quayLocation ? { position: quayLocation, kind: "quay" } : undefined
        }
      />
    </>
  );
};

export const BoardingPositionMarkers = () => {
  const current = useMapCurrentStop();
  const canEditOnMap = useCanEditOnMap(current);
  const focusedElement = useAppSelector(
    (state) =>
      (state as any).mapUtils?.focusedElement as FocusedElement | undefined,
  );
  const focusedBP = useAppSelector(
    (state) =>
      (state as any).mapUtils?.focusedBoardingPositionElement as
        FocusedBoardingPosition | undefined,
  );

  if (!current?.quays?.length) return null;
  if (focusedElement?.type !== "quay" || focusedElement.index < 0) return null;

  const focusedQuayIndex = focusedElement.index;
  const quay = current.quays[focusedQuayIndex];
  if (!quay) return null;

  return (
    <>
      {(quay.boardingPositions ?? []).map((boardingPosition, bpIndex) => (
        <BoardingPositionItem
          key={boardingPosition.id || `${focusedQuayIndex}-${bpIndex}`}
          boardingPosition={boardingPosition}
          bpIndex={bpIndex}
          quayIndex={focusedQuayIndex}
          focused={
            focusedBP?.quayIndex === focusedQuayIndex &&
            focusedBP?.index === bpIndex
          }
          disabled={!canEditOnMap}
          quayLocation={quay.location}
        />
      ))}
    </>
  );
};
